import { abbreviateId, NotificationVariants, QuoteStatusEnum } from '@talos/kyoko';
import { isEqual } from 'lodash-es';
import { setupKeyedListener } from '../../../providers/AppStateProvider/keyedListener';
import { streamingDataSlice } from '../../../providers/AppStateProvider/streamingDataSlice';
import {
  addAppListener,
  type AppListenerEffectAPI,
  type AppStateListenerStart,
} from '../../../providers/AppStateProvider/types';
import { getRFQPricingActions } from './ManualRFQPricingSlice';
import { getRFQPricingSelectors } from './selectors';

export function setupKeyedManualRFQPricingListener(startListening: AppStateListenerStart) {
  setupKeyedListener(startListening, {
    getKeyedState: state => state.manualRFQPricing,
    startInstanceListeners: startManualRFQPricingInstanceListeners,
  });
}

function startManualRFQPricingInstanceListeners(listener: AppListenerEffectAPI, instanceID: string) {
  const selectors = getRFQPricingSelectors(instanceID);
  const actions = getRFQPricingActions(instanceID);

  const initialRFQID = listener.getState().manualRFQPricing[instanceID]?.currentCustomerQuoteRFQID;

  // The instance listeners can be started at a point in time where the RFQID is already populated, meaning that the
  // predicates below watching for a _change_ in the RFQID will not trigger. To still initiate the necessary request,
  // we have a conditional initial sub created here.
  const initialSub = initialRFQID
    ? listener.dispatch(streamingDataSlice.endpoints.getCustomerQuotes.initiate({ RFQID: initialRFQID }))
    : undefined;

  /** Sub and unsub to CustomerQuotes for the RFQID set in the store */
  const unsubscribeCustomerQuoteRequest = listener.dispatch(
    addAppListener({
      predicate: (_, next, prev) => {
        if (selectors.selectIsDestroyed(next)) {
          return false;
        }

        return !isEqual(
          selectors.selectCustomerQuoteRequestParams(prev),
          selectors.selectCustomerQuoteRequestParams(next)
        );
      },
      effect: async (_, listener) => {
        listener.cancelActiveListeners();
        initialSub?.unsubscribe(); // unsubscribing over-and-over is not a problem

        const query = selectors.selectCustomerQuoteRequestParams(listener.getState());
        if (!query) {
          // we cancel the prev listener (unsubbing), and then just return early to not do anything else
          return;
        }

        const customerQuotesSub = listener.dispatch(streamingDataSlice.endpoints.getCustomerQuotes.initiate(query));
        listener.signal.addEventListener('abort', () => {
          customerQuotesSub.unsubscribe();
        });

        // Listener completion calls the abort signal, which would unsub to the customer quote, so we need to await something
        await listener.condition(
          (_, next) => selectors.selectIsDestroyed(next) || selectors.selectCustomerQuoteRequestParams(next) == null
        );
      },
    })
  );

  /** Listen to changes of the customer quote in RTKQ and if there is any change, push it to the slice */
  const unsubscribeCurrentCustomerQuote = listener.dispatch(
    addAppListener({
      predicate: (_, next, prev) => {
        if (selectors.selectIsDestroyed(next)) {
          return false;
        }

        return !isEqual(selectors.selectCustomerQuote(prev), selectors.selectCustomerQuote(next));
      },
      effect: async (_, listener) => {
        const customerQuote = selectors.selectCustomerQuote(listener.getState());
        if (customerQuote) {
          listener.dispatch(actions.setCurrentCustomerQuote(customerQuote));
          listener.dispatch(actions.setIsLoading(false));
        }
      },
    })
  );

  /** On some of the QuoteStatus transitions, we want to show a toast to the user */
  const unsubscribeQuoteStatusChangeListener = listener.dispatch(
    addAppListener({
      predicate: (_, next, prev) => {
        if (selectors.selectIsDestroyed(next)) {
          return false;
        }

        return !isEqual(
          selectors.selectCustomerQuote(prev)?.QuoteStatus,
          selectors.selectCustomerQuote(next)?.QuoteStatus
        );
      },
      effect: async (_, listener) => {
        const customerQuote = selectors.selectCustomerQuote(listener.getState());
        if (!customerQuote) {
          return;
        }

        const addToast = selectors.selectState(listener.getState()).addToast;
        if (customerQuote.QuoteStatus === QuoteStatusEnum.Canceled) {
          addToast?.({
            text: `Quote #${abbreviateId(customerQuote.RFQID)} canceled.`,
            variant: NotificationVariants.Default,
            dismissable: true,
            timeout: 5000,
          });
        }

        if (customerQuote.QuoteStatus === QuoteStatusEnum.Rejected) {
          addToast?.({
            text: customerQuote.Text || `Quote #${abbreviateId(customerQuote.RFQID)} rejected.`,
            variant: NotificationVariants.Warning,
            timeout: 5000,
          });
        }
      },
    })
  );

  return () => {
    initialSub?.unsubscribe();
    listener.dispatch(unsubscribeCustomerQuoteRequest);
    listener.dispatch(unsubscribeCurrentCustomerQuote);
    listener.dispatch(unsubscribeQuoteStatusChangeListener);
  };
}
